import { trackingAxios } from 'config/api/tracking'
import { IOrderDetailRepository } from 'orders/domain/repositories/IOrderDetailRepository'
import { OrderDetailEntity, OrderProgressEntity } from 'orders/domain/entities/order.entity'
import { mapApiResponseToOrderDetail } from 'orders/infrastructure/mappers/orderDetail.mapper'
import { OrderDetailsResponse } from 'interfaces/order'

interface ApiProgressContainer {
    id: number
    code: string
    status: string
    status_description: string
    package_count: number
}

interface ApiProgressChute {
    chute_number: number
    containers: ApiProgressContainer[]
    package_count: number
}

interface ApiProgressRule {
    rule_name: string
    sequence?: number | null
    total_packages: number
    chutes?: ApiProgressChute[]
    containers?: ApiProgressContainer[]
}

interface ApiProgressResponse {
    id: number
    code: string
    state: string
    description: string
    created_at: string
    classification_type: string
    without_containerization?: boolean
    template_name: string
    template_id?: number | null
    last_update: string | null
    total_processed: number
    buyer_identification?: string | null
    rules: ApiProgressRule[]
}

export class OrderDetailHttpRepository implements IOrderDetailRepository {
    async getOrderDetail(orderId: number): Promise<OrderDetailEntity> {
        try {
            const response = await trackingAxios.get<OrderDetailsResponse>(
                `/orders/${orderId}`
            )
            return mapApiResponseToOrderDetail(response.data)
        } catch (error) {
            console.error(
                '[OrderDetailHttpRepository] Error fetching order detail:',
                error
            )
            throw error
        }
    }

    async getOrderProgress(orderId: number, containerCode?: string): Promise<OrderProgressEntity> {
        try {
            const params: Record<string, string> = {}
            if (containerCode) params.container_code = containerCode
            const response = await trackingAxios.get<ApiProgressResponse>(
                `/orders/${orderId}/progress`,
                { params }
            )
            const data = response.data
            return {
                id: data.id,
                code: data.code,
                state: data.state,
                description: data.description,
                createdAt: data.created_at,
                classificationType: data.classification_type,
                withoutContainerization: data.without_containerization ?? false,
                templateName: data.template_name,
                templateId: data.template_id ?? null,
                lastUpdate: data.last_update,
                totalProcessed: data.total_processed,
                rules: (data.rules ?? []).map((r) => ({
                    ruleName: r.rule_name,
                    sequence: typeof r.sequence === 'number' ? r.sequence : null,
                    totalPackages: r.total_packages,
                    chutes: r.chutes?.map((ch) => ({
                        chuteNumber: ch.chute_number,
                        containers: ch.containers?.map((c) => ({
                            id: c.id,
                            code: c.code,
                            status: c.status,
                            statusDescription: c.status_description,
                            packageCount: c.package_count,
                        })),
                        packageCount: ch.package_count,
                    })),
                    containers: r.containers?.map((c) => ({
                        id: c.id,
                        code: c.code,
                        status: c.status,
                        statusDescription: c.status_description,
                        packageCount: c.package_count,
                    })),
                })),
            }
        } catch (error) {
            console.error(
                '[OrderDetailHttpRepository] Error fetching order progress:',
                error
            )
            throw error
        }
    }
}
